import { useCallback, useEffect, useState } from "react";

import { VibeChatStore } from "../stores";
import type { ChatSession as StoreChatSession } from "../stores/types";

import { ChatSessionList } from "./ChatSessionList";
import { ChatSessionPagination } from "./ChatSessionPagination";
import { ChatSessionTrigger } from "./ChatSessionTrigger";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useRecentVibeChatSessions,
  ChatSession as APIChatSession,
  RecentVibeChatSessionsResponse,
} from "@/queries/vibe/useRecentVibeChatSessions";

interface ChatSessionDropdownProps {
  chatStore: VibeChatStore;
  currentChat: ReturnType<typeof VibeChatStore.prototype.chatSessions.find>;
  onNewChat: () => void;
  onChatSelect: (chat: APIChatSession) => void;
  onDeleteChat?: (chatId: string) => void;
  onDeleteAllChats?: () => void;
  isDisabled?: boolean;
  isMaximized?: boolean;
}

export const ChatSessionDropdown = ({
  chatStore,
  currentChat,
  onChatSelect,
  onDeleteChat,
  onDeleteAllChats,
  isDisabled = false,
  isMaximized = false,
}: ChatSessionDropdownProps) => {
  const [page, setPage] = useState(1);
  const perPage = 8;

  const addSessionsFromRequest = useCallback(
    (data: RecentVibeChatSessionsResponse) => {
      if (!data.vibe_chat_sessions.length) {
        return;
      }

      const sessions: StoreChatSession[] = data.vibe_chat_sessions.map(s => ({
        id: s.id,
        title: s.name || "Untitled Chat",
        createdAt: new Date(s.created_at),
        isGeneratingName: s.isGeneratingName,
      }));
      chatStore.addSessionsFromRequest(sessions);
    },
    [chatStore],
  );

  const {
    data: sessionsData,
    isFetching,
    isFetched,
  } = useRecentVibeChatSessions({
    page,
    per_page: perPage,
    onSuccess: addSessionsFromRequest,
  });

  const isSessionsLoading = isFetching && (!isFetched || sessionsData?.page !== page);

  const chatSessions = sessionsData?.vibe_chat_sessions || [];
  const hasNextPage = sessionsData?.has_next ?? false;
  const hasPrevPage = sessionsData?.has_prev ?? false;
  const totalPages = sessionsData?.pages;

  useEffect(() => {
    if (typeof totalPages === "number" && totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const changePage = useCallback(
    (next: number) => {
      if (typeof totalPages === "number" && totalPages > 0) {
        setPage(Math.min(Math.max(1, next), totalPages));
        return;
      }
      setPage(Math.max(1, next));
    },
    [totalPages],
  );

  return (
    <DropdownMenu
      onOpenChange={open => {
        if (!open) {
          setPage(1);
        }
      }}
    >
      <DropdownMenuTrigger
        className="flex min-w-0 items-center gap-1.5 rounded-md border-none bg-transparent px-1.5 py-0.5 text-xs text-gray-600 shadow-none transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
        hideChevron
        disabled={isDisabled}
      >
        <ChatSessionTrigger
          currentChat={currentChat}
          chatSessions={chatSessions}
          currentChatId={chatStore.currentChatId}
          isMaximized={isMaximized}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="z-[100] max-h-[60vh] w-72 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
      >
        <ChatSessionList
          chatSessions={chatSessions}
          isLoading={isSessionsLoading}
          chatStore={chatStore}
          onChatSelect={onChatSelect}
          onDeleteChat={onDeleteChat}
          onDeleteAllChats={onDeleteAllChats}
        />
        <ChatSessionPagination
          page={page}
          totalPages={sessionsData?.pages}
          hasPrevPage={hasPrevPage}
          hasNextPage={hasNextPage}
          onPageChange={changePage}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
